import * as THREE from "three";
import gsap from "gsap";

import { prefersReducedMotion } from "@/scripts/utils/variables";

export const tvShader = {
    rest: { squircle: 1, lens: 0.5, focus: 0.88, edge: 0 },
    hover: { squircle: 0.75, lens: 0.85, focus: 0.8, edge: 0.6 },
    intro: { squircle: 0.75, lens: 1.4, edge: 2.5, duration: 1.6, heroDuration: 1.8, stagger: 0.12 },
    scroll: { page: 0.6, slider: 2, sliderParallax: 0.5, heroUp: 2.2 },
    crossfade: { chromatic: 1.6, edge: 0.8, roll: 0.5 },
    leavePop: { edge: 3, lens: 1.2, chromatic: 2.5, squircle: 0.75, duration: 0.6 },
};

export abstract class ShaderPlaneGroup {
    protected abstract getMeshes(): THREE.Mesh[];

    private lastFlickTime = 0;

    setUniform(name: string, value: number): void {
        for (const mesh of this.getMeshes()) {
            const mat = mesh.material as THREE.ShaderMaterial;
            if (mat.uniforms[name] !== undefined) mat.uniforms[name].value = value;
        }
    }

    animateUniform(name: string, value: number, vars?: gsap.TweenVars): void {
        for (const mesh of this.getMeshes()) {
            const mat = mesh.material as THREE.ShaderMaterial;
            if (mat.uniforms[name] !== undefined) gsap.to(mat.uniforms[name], { value, ...vars });
        }
    }

    protected killUniformTweens(mat: THREE.ShaderMaterial): void {
        for (const uniform of Object.values(mat.uniforms)) {
            gsap.killTweensOf(uniform);
            if (uniform.value instanceof THREE.Vector2) gsap.killTweensOf(uniform.value);
        }
    }

    playLeavePop(): void {
        if (prefersReducedMotion) return;

        this.getMeshes().forEach(mesh => {
            const mat = mesh.material as THREE.ShaderMaterial;

            const { edge, lens, chromatic, squircle, duration } = tvShader.leavePop;

            gsap.to(mat.uniforms.uEdgeBump, { value: edge, duration, ease: "power2.in", overwrite: true });
            gsap.to(mat.uniforms.uLensDistortion, { value: lens, duration, ease: "power2.in", overwrite: true });
            gsap.to(mat.uniforms.uChromaticAberration, { value: chromatic, duration, ease: "power2.in", overwrite: true });
            gsap.to(mat.uniforms.uSquircleProgress, { value: squircle, duration: duration + 0.2, ease: "power2.inOut", overwrite: true });
        });
    }

    protected playChannelFlick(materials: THREE.ShaderMaterial[]): void {
        if (prefersReducedMotion || !materials.length) return;

        const now = performance.now();
        const strength = Math.min(1, (now - this.lastFlickTime) / 400);
        this.lastFlickTime = now;

        materials.forEach(mat => {
            gsap.to(mat.uniforms.uRoll, {
                keyframes: [
                    { value: strength, duration: 0.04, ease: "power2.out" },
                    { value: -strength * 0.5, duration: 0.04, ease: "power1.inOut" },
                    { value: 0, duration: 0.07, ease: "expo.out" },
                ],
                overwrite: true,
            });
            gsap.to(mat.uniforms.uChromaticAberration, {
                keyframes: [
                    { value: 0.5 + strength * 1.2, duration: 0.04, ease: "power2.out" },
                    { value: 0.5, duration: 0.11, ease: "expo.out" },
                ],
                overwrite: true,
            });
        });
    }
}
